import React from 'react'
import styled from 'styled-components'

import { applyTextStyle } from '../../styles/typography'
import {
  countSelectedFilterValuesUnder,
  getFilterParentState,
  ParentSelectionState,
} from '../../utils/filterSelectionMath'
import type { FilterMode, FilterOption, FilterValue } from './Filter'
import FilterGroupHeader from './FilterGroupHeader'
import FilterOptionRow from './FilterOptionRow'

interface IProps<V> {
  listboxId: string
  options: Array<FilterOption<V>>
  mode: FilterMode
  grouped: boolean
  draft: FilterValue<V>
  expanded: Set<unknown>
  activeValue: V | null
  totalSelectedLabel: string
  onToggle: (option: FilterOption<V>) => void
  onToggleExpand: (value: V) => void
  setRowRef: (value: V, el: HTMLElement | null) => void
}

const FilterOptionList = <V,>({
  listboxId,
  options,
  mode,
  grouped,
  draft,
  expanded,
  activeValue,
  totalSelectedLabel,
  onToggle,
  onToggleExpand,
  setRowRef,
}: IProps<V>): JSX.Element => {
  const toggles = options.filter((o) => o.kind === 'toggle')
  const nonToggles = options.filter((o) => o.kind !== 'toggle')

  const renderRow = (
    option: FilterOption<V>,
    depth: number,
    isExpanded: boolean,
    parentState: ParentSelectionState | undefined,
    isParent: boolean
  ) => {
    const childSelectedCount = isParent
      ? countSelectedFilterValuesUnder(option, draft) -
        (option.selectable && draft.includes(option.value) ? 1 : 0)
      : undefined
    return (
      <div key={String(option.value)} ref={(el) => setRowRef(option.value, el)}>
        <FilterOptionRow
          option={option}
          mode={mode}
          depth={depth}
          isParent={isParent}
          isExpanded={isExpanded}
          parentState={parentState}
          selected={draft.includes(option.value)}
          childSelectedCount={childSelectedCount}
          isActive={activeValue === option.value}
          onToggle={() => onToggle(option)}
          onToggleExpand={
            isParent ? () => onToggleExpand(option.value) : undefined
          }
        />
      </div>
    )
  }

  const renderTree = (items: Array<FilterOption<V>>, depth: number) =>
    items.map((option) => {
      const isParent = !!option.children && option.children.length > 0
      const isExpanded = expanded.has(option.value)
      const parentState =
        isParent && mode === 'multi'
          ? getFilterParentState(option, draft)
          : undefined
      return (
        <React.Fragment key={String(option.value)}>
          {renderRow(option, depth, isExpanded, parentState, isParent)}
          {isParent && isExpanded && renderTree(option.children!, depth + 1)}
        </React.Fragment>
      )
    })

  return (
    <ListBody id={listboxId} role="listbox" tabIndex={-1}>
      {toggles.length > 0 && (
        <TogglesSection>
          {toggles.map((toggle) =>
            renderRow(toggle, 0, false, undefined, false)
          )}
        </TogglesSection>
      )}
      {totalSelectedLabel && (
        <SummaryRow>
          <SummaryLabel>{totalSelectedLabel}</SummaryLabel>
        </SummaryRow>
      )}
      {grouped
        ? nonToggles.map((group) => (
            <div key={String(group.value)}>
              <FilterGroupHeader>{group.label}</FilterGroupHeader>
              {group.children && renderTree(group.children, 0)}
            </div>
          ))
        : renderTree(nonToggles, 0)}
    </ListBody>
  )
}

const ListBody = styled.div`
  max-height: 320px;
  overflow-y: auto;
  padding: 4px 0 8px;
`

const TogglesSection = styled.div`
  padding: 4px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.primaryLight};
  margin-bottom: 4px;
`

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 16px 4px;
`

const SummaryLabel = styled.span`
  color: ${({ theme }) => theme.colors.textLight};
  ${applyTextStyle('captionHeavy')}
`

export default FilterOptionList
